/**
 * Offer editing (admin doc 25 §2) and the farming lens (§3.3), split from `Offers.tsx` so each
 * stays readable.
 *
 * **A changed deal is a new offer.** Once a campaign has a redemption, the server accepts only
 * new copy, an earlier end and a larger budget, and refuses the rest with `offer_terms_frozen`.
 * The editor greys the frozen fields when the spend report shows a redemption. That is a hint,
 * though: the server's refusal is the rule, and it is shown verbatim.
 *
 * **The abuse report rules on nothing.** It names shared-device clusters and redemption bursts;
 * pausing an offer, or rejecting an account, happens elsewhere with a reason.
 */
import { useState } from "react";
import { StatusChip } from "../components/primitives";
import { Input, Select } from "../components/forms";
import { DataTable } from "../components/data";
import { EmptyState, ErrorState, LoadingState } from "../components/feedback";
import { ScopeAction, Section } from "../components/kit";
import { useConsole } from "../app/console";
import { ApiError, errorMessage } from "../lib/api/client";
import { parsePaise } from "../lib/money";
import { CloneOffer } from "./OfferRedemptions";
import { OfferScopeFields, sameIds } from "./OfferScope";
import {
  useOfferAbuseReport,
  useUpdateOffer,
  type OfferPatchIn,
  type OfferRow,
} from "../lib/api/hooks/offers";

/** The calendar day an offer ends on, in IST, or '' for open-ended. */
export function endsOnIst(endsAt: string | null): string {
  if (endsAt === null) return "";
  const utc = new Date(
    endsAt.endsWith("Z") || endsAt.includes("+") ? endsAt : `${endsAt}Z`,
  );
  return new Date(utc.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/** The terms sheet as the server stores it: one trimmed, non-blank line per entry. */
export function termLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function OfferEditor({
  offer,
  redeemed,
  onDone,
}: {
  offer: OfferRow;
  redeemed: boolean;
  onDone: () => void;
}) {
  const { scope, toast } = useConsole();
  const update = useUpdateOffer();
  const [name, setName] = useState(offer.name);
  const [title, setTitle] = useState(offer.title);
  const [terms, setTerms] = useState(offer.terms.join("\n"));
  const [budget, setBudget] = useState(
    offer.budget_paise === null ? "" : String(offer.budget_paise / 100),
  );
  const [endsOn, setEndsOn] = useState(endsOnIst(offer.ends_at));
  const [value, setValue] = useState(String(offer.discount_value));
  const [perUser, setPerUser] = useState(String(offer.per_user_limit));
  const [seconder, setSeconder] = useState("");
  const [cityIds, setCityIds] = useState<number[]>(offer.city_ids);
  const [pujaIds, setPujaIds] = useState<number[]>(offer.puja_ids);

  const budgetPaise = parsePaise(budget);
  const endsValid = endsOn === "" || /^\d{4}-\d{2}-\d{2}$/.test(endsOn);
  const needsSecond =
    update.error instanceof ApiError &&
    update.error.code === "second_approval_required";

  const patch = (): OfferPatchIn => {
    const out: OfferPatchIn = {};
    if (name.trim() !== offer.name) out.name = name.trim();
    if (title.trim() !== offer.title) out.title = title.trim();
    const lines = termLines(terms);
    if (lines.join("\n") !== offer.terms.join("\n")) out.terms = lines;
    if (budgetPaise !== offer.budget_paise) out.budget_paise = budgetPaise;
    if (endsOn !== endsOnIst(offer.ends_at))
      out.ends_at = endsOn === "" ? null : `${endsOn}T23:59:59+05:30`;
    if (!redeemed && Number(value) !== offer.discount_value)
      out.discount_value = Number(value);
    if (!redeemed && Number(perUser) !== offer.per_user_limit)
      out.per_user_limit = Number(perUser);
    if (!redeemed && !sameIds(cityIds, offer.city_ids))
      out.city_ids = cityIds;
    if (!redeemed && !sameIds(pujaIds, offer.puja_ids))
      out.puja_ids = pujaIds;
    return out;
  };

  return (
    <Section
      title="Edit campaign"
      note={
        redeemed
          ? "Redeemed already — the value, limits and scope are frozen. Only the copy, an earlier end and a larger budget may change; for a different deal, create a new offer."
          : "Nobody has redeemed this yet, so its terms may still change."
      }
    >
      <Input
        density="admin"
        label="Name (internal)"
        value={name}
        onChange={setName}
      />
      <Input
        density="admin"
        label="Headline (public, linted)"
        value={title}
        onChange={setTitle}
      />
      <Input
        density="admin"
        label="Terms (public, linted)"
        multiline
        value={terms}
        onChange={setTerms}
        helper="One line per term, shown verbatim under the offer's Terms link · blank hides the link"
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "var(--sp-md)",
        }}
      >
        <Input
          density="admin"
          label="Budget"
          prefix="₹"
          inputMode="decimal"
          value={budget}
          onChange={setBudget}
          helper="Blank is uncapped"
        />
        <Input
          density="admin"
          label="Ends on (IST)"
          value={endsOn}
          onChange={setEndsOn}
          placeholder="2026-11-15"
          helper="YYYY-MM-DD · blank runs open-ended"
          error={endsValid ? undefined : "A date as YYYY-MM-DD"}
        />
        <Input
          density="admin"
          label={
            offer.discount_kind === "percent"
              ? "Basis points"
              : "Amount off (paise)"
          }
          inputMode="numeric"
          value={value}
          onChange={setValue}
          disabled={redeemed}
        />
        <Input
          density="admin"
          label="Uses per devotee"
          inputMode="numeric"
          value={perUser}
          onChange={setPerUser}
          disabled={redeemed}
        />
      </div>
      <OfferScopeFields
        cityIds={cityIds}
        pujaIds={pujaIds}
        onCities={setCityIds}
        onPujas={setPujaIds}
        disabled={redeemed}
      />
      {update.isError && (
        <ErrorState
          inline
          title="The server refused this edit"
          cause={errorMessage(update.error)}
        />
      )}
      {needsSecond && (
        <Input
          density="admin"
          label="Second approver (admin id)"
          inputMode="numeric"
          value={seconder}
          onChange={setSeconder}
          helper="A different growth or superadmin — the server refuses your own id"
        />
      )}
      <div style={{ display: "flex", gap: "var(--sp-sm)" }}>
        <ScopeAction
          scope={scope}
          need="growth"
          size="sm"
          disabled={
            !endsValid ||
            (budget.trim() !== "" && budgetPaise === null) ||
            update.isPending
          }
          onClick={() =>
            update.mutate(
              {
                offerId: offer.id,
                patch: patch(),
                ...(seconder === ""
                  ? {}
                  : { second_approver_admin_id: Number(seconder) }),
              },
              {
                onSuccess: () => {
                  toast({ tone: "success", message: "Campaign saved" });
                  onDone();
                },
              },
            )
          }
        >
          Save changes
        </ScopeAction>
        <ScopeAction
          scope={scope}
          need="growth"
          size="sm"
          variant="ghost"
          onClick={onDone}
        >
          Cancel
        </ScopeAction>
      </div>
      {redeemed && <CloneOffer offer={offer} />}
    </Section>
  );
}

const WINDOWS = ["7 days", "30 days", "90 days"];

export function AbuseTab() {
  const [window, setWindow] = useState("30 days");
  const report = useOfferAbuseReport(Number.parseInt(window, 10));
  const data = report.data;

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "var(--sp-md)",
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <Select
          density="admin"
          label="Window"
          options={WINDOWS}
          value={window}
          onChange={setWindow}
          style={{ minWidth: 140 }}
        />
        {data && (
          <span
            style={{ fontSize: "var(--fs-admin-ui)", color: "var(--c-ink2)" }}
          >
            Flagged at {data.device_threshold} accounts on one device · a spike
            is {data.velocity_threshold}+ redemptions of one offer in an hour.
            Read-only: rulings happen in the fraud queue and support.
          </span>
        )}
      </div>
      {report.isPending && (
        <LoadingState rows={3} message="Reading the abuse signals…" />
      )}
      {report.isError && (
        <ErrorState
          title="The abuse report is unavailable"
          cause={errorMessage(report.error)}
        />
      )}
      {data &&
        data.device_clusters.length === 0 &&
        data.velocity_spikes.length === 0 && (
          <EmptyState
            icon="check"
            title="No abuse signals in this window."
            body="No shared devices and no bursts."
          />
        )}
      {data && data.device_clusters.length > 0 && (
        <Section
          title="Shared devices"
          note="Two accounts on one handset is often a family — a watch, not a flag"
          pad={false}
        >
          <DataTable
            density="compact"
            columns={[
              { key: "offer", label: "Offer", mono: true },
              { key: "install", label: "Install", mono: true },
              {
                key: "accounts",
                label: "Accounts",
                align: "right",
                mono: true,
              },
              { key: "uses", label: "Redemptions", align: "right", mono: true },
              { key: "users", label: "User ids", mono: true },
              { key: "state", label: "State" },
            ]}
            rows={data.device_clusters.map((c) => ({
              id: `${c.offer_id}-${c.install_id}`,
              offer: c.offer_slug,
              install: c.install_id,
              accounts: String(c.user_ids.length),
              uses: String(c.redemption_count),
              users: c.user_ids.join(", "),
              state: (
                <StatusChip
                  tone={c.flagged ? "error" : "warn"}
                  label={c.flagged ? "flagged" : "watch"}
                />
              ),
            }))}
          />
        </Section>
      )}
      {data && data.velocity_spikes.length > 0 && (
        <Section
          title="Redemption bursts"
          note="A leaked code shows up as a burst, not a trend"
          pad={false}
        >
          <DataTable
            density="compact"
            columns={[
              { key: "offer", label: "Offer", mono: true },
              { key: "hour", label: "Hour (UTC)", mono: true },
              {
                key: "count",
                label: "Redemptions",
                align: "right",
                mono: true,
              },
            ]}
            rows={data.velocity_spikes.map((s) => ({
              id: `${s.offer_id}-${s.hour}`,
              offer: s.offer_slug,
              hour: s.hour.slice(0, 16).replace("T", " "),
              count: String(s.redemption_count),
            }))}
          />
        </Section>
      )}
    </>
  );
}
